import { useMemo } from "react";
import { Briefcase, Cake, Clock3, Code2, Globe2, Heart, MapPin, MessageCircle, MessageSquare, Moon, Phone, Sun, Sunrise, Sunset, UserRound } from "lucide-react";
import { Chip, Field, FormSection, Tile, areaCls, inputCls, selectCls } from "@/components/forms/FormKit";
import { countryNames } from "@/lib/countries";
import { DAYS, DAY_PRESETS, HALF_HOURS, fmtHHMM, timeZones } from "@/lib/dates";

const REASON_ICONS = { relationship: Heart, career: Briefcase, tech: Code2, other: MessageCircle };
const TIME_WINDOWS = [
  { id: "morning", label: "Morning", from: "09:00", to: "12:00", Icon: Sunrise },
  { id: "afternoon", label: "Afternoon", from: "12:00", to: "17:00", Icon: Sun },
  { id: "evening", label: "Evening", from: "18:00", to: "21:00", Icon: Sunset },
  { id: "night", label: "Late night", from: "21:00", to: "00:00", Icon: Moon },
];
const subLabel = "text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-400";

export const ageOk = (v) => Number(v) >= 10 && Number(v) <= 100;
export const phoneOk = (v) => (String(v).match(/\d/g) || []).length >= 6;

export const sectionDone = (f) => ({
  about: f.name.trim().length >= 2 && ageOk(f.age) && !!f.country && phoneOk(f.phone),
  topic: !!f.reason && f.notes.trim().length >= 10,
  time: f.days.length > 0 && !!f.from && !!f.to && f.from !== f.to && !!f.timezone,
});

export function validateBooking(f) {
  if (f.name.trim().length < 2) return "Please enter your full name";
  if (!ageOk(f.age)) return "Please enter your age (10 to 100)";
  if (!f.country) return "Please choose your country";
  if (!phoneOk(f.phone)) return "Please enter your phone or WhatsApp number";
  if (!f.reason) return "Pick what you'd like help with";
  if (f.notes.trim().length < 10) return "Tell Sankalp a little more (at least 10 characters)";
  if (!f.days.length) return "Pick at least one preferred day";
  if (!f.from || !f.to || f.from === f.to) return "Pick a time window";
  return null;
}

export const toDetails = (f) => ({
  name: f.name.trim(), age: Number(f.age), country: f.country, phone: f.phone.trim(), reason: f.reason, notes: f.notes.trim(),
  preferred_days: f.days, time_from: f.from, time_to: f.to, timezone: f.timezone,
});

export function AboutYouSection({ form, set, done }) {
  const countries = useMemo(countryNames, []);
  return (
    <FormSection n="01" title="About you" hint="So Sankalp knows who he's meeting." done={done} testid="one-on-one-section-about">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" htmlFor="oo-name" required icon={UserRound} filled={form.name.trim().length >= 2} className="sm:col-span-2">
          <input id="oo-name" data-testid="one-on-one-name" value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} autoComplete="name" placeholder="Your full name" className={inputCls} />
        </Field>
        <Field label="Age" htmlFor="oo-age" required icon={Cake} filled={ageOk(form.age)}>
          <input id="oo-age" data-testid="one-on-one-age" type="number" inputMode="numeric" min={10} max={100} value={form.age} onChange={(e) => set({ age: e.target.value })} placeholder="e.g. 27" className={inputCls} />
        </Field>
        <Field label="Country" htmlFor="oo-country" required icon={MapPin} select>
          <select id="oo-country" data-testid="one-on-one-country" value={form.country} onChange={(e) => set({ country: e.target.value })} className={selectCls}>
            <option value="">Select your country</option>
            {countries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Phone / WhatsApp" htmlFor="oo-phone" required icon={Phone} filled={phoneOk(form.phone)} className="sm:col-span-2">
          <input id="oo-phone" data-testid="one-on-one-phone" type="tel" autoComplete="tel" maxLength={40} value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+91 98xxxxxx10 (with country code)" className={inputCls} />
        </Field>
      </div>
    </FormSection>
  );
}

export function TopicSection({ form, set, done, reasons }) {
  return (
    <FormSection n="02" title="What's on your mind?" hint="Pick a topic and share a few lines so Sankalp can prepare." done={done} testid="one-on-one-section-topic">
      <div className="grid grid-cols-2 gap-2.5">
        {reasons.map((r) => (
          <Tile key={r.value} testid={`one-on-one-reason-${r.value}`} on={form.reason === r.value} onClick={() => set({ reason: r.value })} icon={REASON_ICONS[r.value] || MessageCircle} title={r.label} />
        ))}
      </div>
      <Field label="Tell Sankalp a little about it" htmlFor="oo-notes" required icon={MessageSquare} top filled={form.notes.trim().length >= 10} className="mt-5">
        <textarea id="oo-notes" data-testid="one-on-one-notes" rows={4} maxLength={1500} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="e.g. I'm 2 years into a data role and want to move into product. Where do I start?" className={areaCls} />
      </Field>
      <div className="mt-1.5 text-right text-[10px] font-adult-mono text-zinc-600">{form.notes.length}/1500</div>
    </FormSection>
  );
}

function DayPicker({ days, set }) {
  const same = (list) => list.length === days.length && list.every((d) => days.includes(d));
  const toggle = (d) => set({ days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d] });
  return (
    <div>
      <div className={subLabel}>Preferred days <span className="text-amber-500">*</span></div>
      <div className="mt-2 flex flex-wrap gap-2">
        {DAY_PRESETS.map((p) => <Chip key={p.id} testid={`one-on-one-days-${p.id}`} on={same(p.days)} onClick={() => set({ days: p.days })}>{p.label}</Chip>)}
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1.5">
        {DAYS.map(([d, l]) => {
          const on = days.includes(d);
          return (
            <button key={d} type="button" data-testid={`one-on-one-day-${d}`} aria-pressed={on} onClick={() => toggle(d)}
              className={`h-11 rounded-lg border text-xs font-adult-mono transition-[background-color,border-color,color] duration-200 ${on ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"}`}>
              {l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TimeSelect({ id, label, value, onChange, testid }) {
  return (
    <Field label={label} htmlFor={id} icon={Clock3} select>
      <select id={id} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
        <option value="">Select</option>
        {HALF_HOURS.map((t) => <option key={t} value={t}>{fmtHHMM(t)}</option>)}
      </select>
    </Field>
  );
}

export function TimeSection({ form, set, done }) {
  const zones = useMemo(timeZones, []);
  return (
    <FormSection n="03" title="When suits you?" hint="Share your preferred days and time. Sankalp's team confirms the exact slot." done={done} last testid="one-on-one-section-time">
      <DayPicker days={form.days} set={set} />
      <div className={`${subLabel} mt-6`}>Time window <span className="text-amber-500">*</span></div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {TIME_WINDOWS.map((w) => (
          <Tile key={w.id} testid={`one-on-one-window-${w.id}`} on={form.window === w.id} onClick={() => set({ window: w.id, from: w.from, to: w.to })} icon={w.Icon} title={w.label} sub={`${fmtHHMM(w.from)} – ${fmtHHMM(w.to)}`} />
        ))}
        <Tile testid="one-on-one-window-custom" on={form.window === "custom"} onClick={() => set({ window: "custom" })} icon={Clock3} title="Custom range" sub="Set your own hours" className="col-span-2" />
      </div>
      {form.window === "custom" && (
        <div className="mt-3 grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-1 duration-300">
          <TimeSelect id="oo-from" label="From" value={form.from} onChange={(from) => set({ from })} testid="one-on-one-time-from" />
          <TimeSelect id="oo-to" label="To" value={form.to} onChange={(to) => set({ to })} testid="one-on-one-time-to" />
        </div>
      )}
      <Field label="Your time zone" htmlFor="oo-tz" required icon={Globe2} select className="mt-6">
        <select id="oo-tz" data-testid="one-on-one-timezone" value={form.timezone} onChange={(e) => set({ timezone: e.target.value })} className={selectCls}>
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
        </select>
      </Field>
      <p className="mt-1.5 text-[11px] text-zinc-500">Detected from your device. Change it if you&apos;ll be somewhere else.</p>
    </FormSection>
  );
}
